import { useEffect, useRef } from 'react'

/**
 * The right-click menu.
 *
 * **Nothing is only here**, and that is the rule it is built under rather than a caveat: every entry
 * on it is also a control in the panel beside the board or in one of the two bars. A menu that
 * nobody can open on a touch screen must not be the only way to reach anything, and MyLib says
 * elsewhere that nothing may be reachable by gesture alone either.
 *
 * It exists all the same because on a Mac or a PC people *reach for it*. Right-clicking something on
 * a surface and getting the browser's own menu — reload, view source, save image — is the moment an
 * application stops feeling like one.
 *
 * It closes on anything that is not a choice: Escape, a press elsewhere, the wheel, the board moving
 * underneath it. A menu that outlives what it was about is worse than none.
 */
export interface Entry {
  readonly id: string
  readonly label: string
  /** Set on the one entry that removes something, so it can be told apart at a glance. */
  readonly danger?: boolean
  readonly run: () => void
}

export interface MenuProps {
  /** Where it was asked for, in screen coordinates within the board's pane. */
  readonly at: { readonly x: number; readonly y: number }
  readonly entries: readonly Entry[]
  readonly onClose: () => void
}

export function Menu({ at, entries, onClose }: MenuProps) {
  const first = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    // Straight onto the first entry: a menu opened by a pointer is still a list somebody may want to
    // walk with the arrow keys, and it is the only thing on the screen that is about them right now.
    first.current?.focus()
  }, [])

  useEffect(() => {
    const away = () => {
      onClose()
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
      }
    }
    // `pointerdown` and not `click`: the press that lands outside is the moment it stopped being
    // about this menu, and waiting for the release would let that press do two things at once.
    window.addEventListener('pointerdown', away)
    window.addEventListener('wheel', away)
    window.addEventListener('keydown', onKey, true)
    return () => {
      window.removeEventListener('pointerdown', away)
      window.removeEventListener('wheel', away)
      window.removeEventListener('keydown', onKey, true)
    }
  }, [onClose])

  if (entries.length === 0) {
    return null
  }

  return (
    <div
      className="board__menu"
      role="menu"
      style={{ left: `${at.x}px`, top: `${at.y}px` }}
      // Its own presses are its own business; the listener above is for everything else.
      onPointerDown={(event) => {
        event.stopPropagation()
      }}
    >
      {entries.map((entry, index) => (
        <button
          key={entry.id}
          ref={index === 0 ? first : undefined}
          type="button"
          role="menuitem"
          className={entry.danger === true ? 'board__menuItem control--danger' : 'board__menuItem'}
          onClick={() => {
            entry.run()
            onClose()
          }}
        >
          {entry.label}
        </button>
      ))}
    </div>
  )
}
